import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Headset, Sprout, ShieldCheck } from "lucide-react";
import { API, api, img, inr, errMsg } from "../../lib/api";
import { useShop } from "../../context/ShopContext";

const sid = () => {
  let s = localStorage.getItem("seed_chat_sid");
  if (!s) {
    s = (crypto.randomUUID?.() || String(Date.now()) + Math.random()).replace(/[^a-z0-9]/gi, "").slice(0, 32);
    localStorage.setItem("seed_chat_sid", s);
  }
  return s;
};

function ProductMini({ p }) {
  const { addToCart } = useShop();
  return (
    <div className="flex gap-2 bg-white border border-[#E8E0D5] rounded-xl p-2 mt-2" data-testid={`chat-product-${p.slug}`}>
      <img src={img(p.images?.[0]?.url)} alt={p.name} className="w-14 h-14 rounded-lg object-cover" />
      <div className="flex-1 min-w-0">
        <Link to={`/product/${p.slug}`} className="text-xs font-medium text-stone-900 line-clamp-2 hover:underline">{p.name}</Link>
        <p className="text-xs mt-0.5"><b className="text-[#1B3B2B]">{inr(p.price)}</b> {p.mrp > p.price && <s className="text-stone-400">{inr(p.mrp)}</s>}</p>
        <button disabled={p.is_demo || p.stock <= 0} onClick={() => addToCart({ ...p, variants: [] }, null, 1, false)} data-testid={`chat-add-to-cart-${p.slug}`}
          className="mt-1 text-[11px] bg-[#1B3B2B] text-white rounded-full px-3 py-1 disabled:opacity-40">{p.is_demo ? "Not available" : p.stock > 0 ? "Add to cart" : "Out of stock"}</button>
      </div>
    </div>
  );
}

function ConsentCard({ captured, onDone, hi }) {
  const [f, setF] = useState({ name: captured?.name || "", mobile: captured?.mobile || "", city: captured?.city || "", pincode: captured?.pincode || "" });
  const [followup, setFollowup] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (agree) => {
    setErr("");
    try {
      await api.post("/chat/consent", { session_id: sid(), followup: agree && followup, marketing: agree && marketing, ...(agree ? f : {}) });
      onDone(agree && followup);
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  const inp = "w-full border border-[#E8E0D5] rounded-lg px-2.5 py-1.5 text-xs bg-white outline-none focus:border-[#6B8E78]";
  return (
    <div className="bg-[#E2EBE2]/60 border border-[#cfdccf] rounded-xl p-3 space-y-2 text-xs" data-testid="chat-consent-card">
      <p className="flex items-center gap-1.5 font-medium text-[#1B3B2B]"><ShieldCheck className="w-4 h-4" /> {hi ? "आपकी जानकारी सुरक्षित है" : "Your details stay private"}</p>
      <p className="text-stone-600">{hi ? "हम आपकी जानकारी सिर्फ़ आपकी पूछताछ/ऑर्डर में मदद के लिए सेव करेंगे। यह वैकल्पिक है।" : "We save these only so our team can help with your enquiry or order. Sharing is optional."}</p>
      <div className="grid grid-cols-2 gap-2">
        <input className={inp} placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="chat-lead-name" />
        <input className={inp} placeholder="Mobile (10 digits)" inputMode="numeric" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="chat-lead-mobile" />
        <input className={inp} placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="chat-lead-city" />
        <input className={inp} placeholder="PIN code" inputMode="numeric" value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} data-testid="chat-lead-pincode" />
      </div>
      <label className="flex gap-2 items-start"><input type="checkbox" checked={followup} onChange={(e) => setFollowup(e.target.checked)} data-testid="chat-consent-followup" /> Save my details so SEED can contact me about this enquiry</label>
      <label className="flex gap-2 items-start"><input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} data-testid="chat-consent-marketing" /> Send me offers and updates (optional)</label>
      {err && <p className="text-[#B91C1C]">{err}</p>}
      <div className="flex gap-2">
        <button onClick={() => submit(true)} className="flex-1 bg-[#1B3B2B] text-white rounded-full py-1.5" data-testid="chat-consent-save">Save</button>
        <button onClick={() => submit(false)} className="flex-1 border border-[#1B3B2B] text-[#1B3B2B] rounded-full py-1.5" data-testid="chat-consent-decline">No thanks</button>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [lang, setLang] = useState("en");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(null);
  const [support, setSupport] = useState(null);
  const end = useRef(null);
  const hi = lang === "hi";

  useEffect(() => {
    api.get("/chat/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
    api.get(`/chat/session/${sid()}`).then((r) => setMsgs(r.data.messages || [])).catch(() => {});
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, consent, open]);

  if (!cfg || cfg.enabled === false) return null;

  const send = async (t) => {
    const message = (t ?? text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "", products: [] }]);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const res = await fetch(`${API}/chat/message`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid(), message, language: lang }) });
      if (!res.ok) throw new Error((await res.json()).detail || "Chat unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.t === "delta") patch((x) => ({ ...x, content: x.content + ev.c }));
          if (ev.t === "done") {
            patch((x) => ({ ...x, products: ev.products }));
            if (ev.ask_consent) setConsent(ev.captured);
          }
        }
      }
    } catch (e) {
      patch((x) => ({ ...x, content: hi ? "माफ़ कीजिए, अभी जवाब नहीं दे पा रहे। कृपया फिर कोशिश करें।" : `Sorry, something went wrong. ${e.message}` }));
    }
    setBusy(false);
  };
  const handover = async () => {
    const { data } = await api.post("/chat/handover", { session_id: sid() });
    setSupport(data.support || {});
  };
  const quick = (hi ? cfg.quick_replies_hi : cfg.quick_replies_en) || [];

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="chatbot-open-trigger" aria-label="Open SEED Beauty Assistant"
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 rounded-full bg-[#1B3B2B] text-[#FBF8F3] grid place-items-center shadow-lg hover:scale-105 transition-transform ${open ? "" : "pulse-ring"}`}>
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      {open && (
        <div data-testid="chatbot-window" className="fixed z-50 inset-x-2 bottom-20 top-16 sm:inset-auto sm:right-6 sm:bottom-24 sm:w-[380px] sm:h-[min(600px,calc(100vh-8rem))] flex flex-col rounded-2xl overflow-hidden border border-white/60 bg-[#FFFDF9]/95 backdrop-blur-xl shadow-2xl fade-up">
          <div className="bg-[#1B3B2B] text-[#FBF8F3] px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#6B8E78] grid place-items-center"><Sprout className="w-5 h-5" /></div>
            <div className="flex-1">
              <p className="font-serif text-lg leading-none">SEED Beauty Assistant</p>
              <p className="text-[11px] opacity-70 mt-1">{hi ? "हिंदी या English में पूछें" : "Ask in English or Hindi"}</p>
            </div>
            <div className="flex bg-white/10 rounded-full p-0.5 text-[11px]">
              {["en", "hi"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`chatbot-lang-${l}`} className={`px-2.5 py-1 rounded-full ${lang === l ? "bg-[#FBF8F3] text-[#1B3B2B]" : ""}`}>{l === "en" ? "EN" : "हिं"}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="chatbot-messages">
            <div className="bg-[#E2EBE2] rounded-2xl rounded-tl-sm px-3.5 py-2.5 max-w-[85%] text-[#1B3B2B]" data-testid="chatbot-welcome">{hi ? cfg.welcome_hi : cfg.welcome_en}</div>
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className={`px-3.5 py-2.5 max-w-[88%] whitespace-pre-wrap rounded-2xl ${m.role === "user" ? "bg-[#1B3B2B] text-[#FBF8F3] rounded-tr-sm" : "bg-white border border-[#E8E0D5] text-stone-800 rounded-tl-sm"}`} data-testid={`chat-msg-${m.role}`}>
                  {m.content?.replace(/\*\*/g, "") || (busy && i === msgs.length - 1 ? <span className="animate-pulse">…</span> : "")}
                  {m.products?.map((p) => <ProductMini key={p.id} p={p} />)}
                </div>
              </div>
            ))}
            {consent && <ConsentCard captured={consent} hi={hi} onDone={(saved) => { setConsent(null); setMsgs((m) => [...m, { role: "assistant", content: saved ? (hi ? "धन्यवाद! हमारी टीम ज़रूरत होने पर आपसे संपर्क करेगी।" : "Thank you! Our team may reach out to help you.") : (hi ? "कोई बात नहीं, आप बिना जानकारी दिए भी ब्राउज़ कर सकते हैं।" : "No problem — you can keep browsing without sharing details.") }]); }} />}
            {support && (
              <div className="bg-[#F4EFE6] rounded-xl p-3 text-xs space-y-1" data-testid="chat-support-card">
                <p className="font-medium text-[#1B3B2B]">{hi ? "हमारी टीम से बात करें" : "Talk to our team"}</p>
                {support.whatsapp && <a className="block underline" href={`https://wa.me/91${support.whatsapp}`} target="_blank" rel="noreferrer">WhatsApp: {support.whatsapp}</a>}
                {support.phone && <a className="block underline" href={`tel:${support.phone}`}>Call: {support.phone}</a>}
                {support.email && <a className="block underline" href={`mailto:${support.email}`}>Email: {support.email}</a>}
                <p className="text-stone-500">{hi ? "हमने आपका अनुरोध टीम को भेज दिया है।" : "We've notified our team about your request."}</p>
              </div>
            )}
            <div ref={end} />
          </div>
          <div className="px-3 pt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {quick.map((q) => <button key={q} onClick={() => send(q)} className="shrink-0 text-[11px] border border-[#cfdccf] bg-[#E2EBE2]/50 text-[#1B3B2B] rounded-full px-3 py-1.5 hover:bg-[#E2EBE2]" data-testid="chatbot-quick-reply">{q}</button>)}
            <button onClick={() => setConsent({})} className="shrink-0 text-[11px] border border-[#E8E0D5] rounded-full px-3 py-1.5" data-testid="chatbot-share-details">{hi ? "अपनी जानकारी दें" : "Request a callback"}</button>
            <button onClick={handover} className="shrink-0 text-[11px] border border-[#E8E0D5] rounded-full px-3 py-1.5 flex items-center gap-1" data-testid="chatbot-human-support"><Headset className="w-3 h-3" /> {hi ? "इंसान से बात" : "Human support"}</button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} placeholder={hi ? "अपना सवाल लिखें…" : "Type your question…"} data-testid="chatbot-input"
              className="flex-1 min-w-0 border border-[#E8E0D5] rounded-full px-4 py-2.5 text-sm bg-white outline-none focus:border-[#6B8E78]" />
            <button disabled={busy} className="w-10 h-10 rounded-full bg-[#1B3B2B] text-white grid place-items-center disabled:opacity-50" data-testid="chatbot-send-button" aria-label="Send"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
}
